import { useMemo } from "react";

import { Text } from "@alpacahq/ui/old";

import { useGetCryptoFeeStructure } from "../../../../api/hooks/useGetCryptoFeeStructure";
import { useGetCryptoMonthlyVolume } from "../../../../api/hooks/useGetCryptoMonthlyVolume";
import {
  getPrettyAmount,
  getPrettyCash,
  getPrettyPercent,
} from "../../../../utils/formatting";
import type { SettingsTabProps } from "../index";

export default ({ account }: SettingsTabProps) => {
  const { volume } = useGetCryptoMonthlyVolume({ accountID: account.id });
  const { fee_structure } = useGetCryptoFeeStructure();

  // Updated fee structure is going live at an inopportune time to launch the frontend. Checks if API is sending new structure
  const isDisplayingMakerTaker = useMemo(
    () =>
      Object.entries(fee_structure || {}).some(
        ([_key, value]) =>
          Object.hasOwn(value, "maker") && Object.hasOwn(value, "taker"),
      ),
    [fee_structure],
  );

  const rows = useMemo(
    () =>
      Object.entries(fee_structure || {}).map(([key, value], index, array) => {
        const isLastItem = index === array.length - 1;

        // On last item display `{volume}+`, else display range from current to next volumes
        const volume = isLastItem
          ? `${getPrettyAmount(value.volume, 0, 0)}+`
          : `${
              !Number(value.volume) ? "0" : getPrettyAmount(value.volume, 0, 0)
            } - ${getPrettyAmount(array[index + 1][1].volume)}`;

        // Pretty function returns `-` for `0` values, need to display `0.00%`
        const displayPercentage = (value = "") =>
          !Number(value) ? "0.00%" : getPrettyPercent(value, true);

        return (
          <tr key={key}>
            <td className="p-2 text-center">{key}</td>
            <td className="p-2 text-center">{volume}</td>
            {isDisplayingMakerTaker && (
              <>
                <td className="p-2 text-center">
                  {displayPercentage(value?.maker)}
                </td>
                <td className="p-2 text-center">
                  {displayPercentage(value?.taker)}
                </td>
              </>
            )}
          </tr>
        );
      }),
    [fee_structure, isDisplayingMakerTaker],
  );

  return (
    <div>
      <section>
        <h2 className="mb-5 font-bold text-xl">Level</h2>
        <div className="flex w-full max-w-[600px] items-center justify-center gap-8 rounded bg-gray-100 p-4 text-sm">
          <Text className="py-2 font-semibold text-black">
            Level {account?.crypto_tier ?? 1}
          </Text>
          <div className="flex grow flex-col gap-2">
            <Text>
              Crypto Spot Trading volume (last 30 days as of{" "}
              {new Date(
                new Date().setDate(new Date().getDate() - 1),
              ).toLocaleDateString()}
              )
            </Text>
            <Text className="font-medium">
              {!volume ? "$0.00" : getPrettyCash(volume)}
            </Text>
          </div>
        </div>
      </section>
      <section>
        <h2 className="my-5 font-bold text-xl">Fee Rates</h2>

        <table className="w-full max-w-[600px] rounded bg-gray-100 text-sm">
          <thead>
            <tr>
              <th className="p-2 text-center">Level</th>
              <th className="p-2 text-center">
                Crypto Spot Trading Volume Last 30 Days (USD)
              </th>
              {isDisplayingMakerTaker && (
                <>
                  <th className="p-2 text-center">Maker</th>
                  <th className="p-2 text-center">Taker</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </section>
    </div>
  );
};
